import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortBy from 'lodash/sortBy';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { getManagementOrganizations, getManagementUsers } from '../../../../store/management';

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, subvalue, valueColor }) {
  return (
    <Card variant='outlined' sx={{ flex: 1, minWidth: 130 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            fontSize: 10,
            display: 'block',
            mb: 0.5,
          }}>
          {label}
        </Typography>
        <Typography
          variant='h5'
          fontWeight={700}
          color={valueColor ?? 'text.primary'}
          sx={{ lineHeight: 1 }}>
          {value}
        </Typography>
        {subvalue && (
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
            {subvalue}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Console ──────────────────────────────────────────────────────────────────

export default function Console() {
  const dispatch = useDispatch();
  const { organizations, users } = useSelector((state) => state.management);

  useEffect(() => {
    dispatch(getManagementOrganizations());
    dispatch(getManagementUsers());
  }, [dispatch]);

  const rows = useMemo(() => sortBy(organizations, (o) => o.name ?? ''), [organizations]);

  return (
    <Box sx={{ maxWidth: 900, width: '100%', px: 1, mt: 1, mb: 5 }}>

      {/* ── KPI row ──────────────────────────────────────────────────── */}
      <Stack direction='row' spacing={1.5} sx={{ mb: 3 }} flexWrap='wrap'>
        <StatCard label='Organizations' value={organizations.length} />
        <StatCard label='Users' value={users.length} />
      </Stack>

      {/* ── Orgs table ───────────────────────────────────────────────── */}
      <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
        Organizations
      </Typography>
      <Card variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((org) => (
              <TableRow key={org.org_id} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {org.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='caption' color='text.secondary'>
                    {org.org_id}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  <Typography variant='body2' color='text.secondary'>
                    No organizations
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}